/**
 * Rendered slide images of a v2 deck template (PNG at 1600 px and 320 px thumbnails in the private
 * `presentation-renders` bucket), read through short-lived signed URLs.
 *
 * Where the paths come from (see cloud-run-worker/rendererClient.js for the folder layout), first
 * match wins:
 * 1. `slide.render = {page, png, thumb}` on a template slide (what deckImport writes);
 * 2. `render.prefix` of the template (or `generation_metadata.renderPrefix`) with the renderer's
 *    names `<prefix>slides/001.png` and `<prefix>thumbs/001.png`, by render page (1-based page of
 *    the rendered PDF; hidden slides have none).
 * Nothing is signed while the render is pending or failed, for another bucket, or outside the
 * active account's folder (the first path segment is the account id).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { slidesOf } from './deckModel';

/** Private bucket of slide renders. */
export const RENDERS_BUCKET = 'presentation-renders';

/** Signed URL lifetime (seconds). */
export const SIGNED_URL_SECONDS = 60 * 60;

/** Paths per createSignedUrls call. */
const SIGN_BATCH = 100;

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);

/**
 * Renderer file name of a page.
 * @param {number} page - 1-based page.
 * @returns {string} `001.png`.
 */
export function renderPageName(page) {
  return `${String(page).padStart(3, '0')}.png`;
}

/**
 * Whether a storage path is safe to sign for this account.
 * @param {unknown} path - Object path inside the bucket.
 * @param {string|null|undefined} accountId - Active account id (skips the folder check when absent).
 * @returns {boolean} True when the path can be used.
 */
export function isAllowedRenderPath(path, accountId) {
  if (typeof path !== 'string' || !path || path.length > 1024) return false;
  if (path.startsWith('/') || path.includes('\\') || /(^|\/)\.\.?(\/|$)/.test(path) || /[?#]/.test(path)) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return false;
  if (accountId && path.split('/')[0].toLowerCase() !== String(accountId).toLowerCase()) return false;
  return true;
}

/**
 * The render block of a template: the template's own `render`, else the import metadata's
 * (`generation_metadata.render`, or its `renderStatus` / `renderPrefix`).
 * @param {object} model - Deck template.
 * @param {object|null|undefined} generationMetadata - `templates.generation_metadata`.
 * @returns {object|null} Render block `{status, bucket, prefix}`.
 */
export function renderBlockOf(model, generationMetadata) {
  if (isObject(model?.render)) return model.render;
  if (isObject(generationMetadata?.render)) return generationMetadata.render;
  if (generationMetadata?.renderStatus || generationMetadata?.renderPrefix) {
    return { status: generationMetadata.renderStatus || null, bucket: generationMetadata.renderBucket || null, prefix: generationMetadata.renderPrefix || null };
  }
  return null;
}

/**
 * State of the slide images.
 * @param {object} model - Deck template.
 * @param {object|null|undefined} generationMetadata - Import metadata.
 * @returns {'ready'|'pending'|'failed'|'none'} Status.
 */
export function renderStatusOf(model, generationMetadata) {
  const block = renderBlockOf(model, generationMetadata);
  const hasSlidePaths = (model?.slides || []).some((s) => s?.render?.png);
  if (hasSlidePaths) return 'ready';
  if (block?.status === 'pending' || block?.status === 'failed' || block?.status === 'ready') return block.status;
  return block ? 'ready' : 'none';
}

/**
 * Folder prefix ending in '/', or ''.
 * @param {unknown} prefix - Stored prefix.
 * @returns {string} Prefix.
 */
const folderOf = (prefix) => (typeof prefix === 'string' && prefix ? `${prefix.replace(/\/+$/, '')}/` : '');

/**
 * Storage paths of every slide's image and thumbnail.
 * @param {object} model - Deck template.
 * @param {object|null|undefined} generationMetadata - Import metadata.
 * @param {string|null|undefined} accountId - Active account id.
 * @returns {Map<string, {png: string|null, thumb: string|null}>} Slide key to paths.
 */
export function resolveSlideImagePaths(model, generationMetadata, accountId) {
  const block = renderBlockOf(model, generationMetadata);
  const usable = !(block?.bucket && block.bucket !== RENDERS_BUCKET);
  const ready = usable && block?.status !== 'pending' && block?.status !== 'failed';
  const prefix = folderOf(block?.prefix);
  const out = new Map();
  slidesOf(model).forEach((slide) => {
    const own = isObject(slide.render) ? slide.render : {};
    const page = Number.isInteger(own.page) && own.page > 0 ? own.page : slide.renderPage;
    const pick = (kind) => {
      if (!usable) return null;
      if (typeof own[kind] === 'string' && own[kind]) return own[kind];
      if (!ready || !page || !prefix || slide.hidden) return null;
      return `${prefix}${kind === 'png' ? 'slides' : 'thumbs'}/${renderPageName(page)}`;
    };
    const png = pick('png');
    const thumb = pick('thumb');
    out.set(slide.key, {
      png: isAllowedRenderPath(png, accountId) ? png : null,
      thumb: isAllowedRenderPath(thumb, accountId) ? thumb : null,
    });
  });
  return out;
}

/**
 * Path of a deck's cover thumbnail (template list cards), from the import metadata
 * (`{renderStatus, renderPrefix}`) or a render block (`{status, prefix}`).
 * @param {object|null|undefined} source - Metadata subset or render block.
 * @param {string|null|undefined} accountId - Active account id.
 * @returns {string|null} Path.
 */
export function coverThumbPath(source, accountId) {
  if (!isObject(source)) return null;
  const status = source.renderStatus ?? source.status;
  const bucket = source.renderBucket ?? source.bucket;
  if (status !== 'ready' || (bucket && bucket !== RENDERS_BUCKET)) return null;
  const prefix = folderOf(source.renderPrefix ?? source.prefix);
  const path = prefix ? `${prefix}thumbs/${renderPageName(1)}` : null;
  return isAllowedRenderPath(path, accountId) ? path : null;
}

/**
 * Sign storage paths in batches.
 * @param {object} client - Supabase client.
 * @param {string[]} paths - Paths in the renders bucket.
 * @param {number} [expiresIn] - Seconds.
 * @returns {Promise<Map<string, string>>} Path to signed URL (paths that failed are left out).
 */
export async function signRenderPaths(client, paths, expiresIn = SIGNED_URL_SECONDS) {
  const unique = [...new Set(paths.filter(Boolean))];
  const out = new Map();
  if (unique.length === 0 || typeof client?.storage?.from !== 'function') return out;
  const bucket = client.storage.from(RENDERS_BUCKET);
  if (typeof bucket.createSignedUrls !== 'function') return out;
  for (let i = 0; i < unique.length; i += SIGN_BATCH) {
    const chunk = unique.slice(i, i + SIGN_BATCH);
    const { data, error } = await bucket.createSignedUrls(chunk, expiresIn);
    if (error) throw error;
    (data || []).forEach((item, index) => {
      const url = item?.signedUrl || item?.signedURL;
      const path = item?.path || chunk[index];
      if (url && !item?.error) out.set(path, url);
    });
  }
  return out;
}

/**
 * Signed image URLs for every slide of a deck template.
 * @param {object} args - Arguments.
 * @param {object} args.model - Deck template.
 * @param {object|null|undefined} args.generationMetadata - Import metadata.
 * @param {string|null|undefined} args.accountId - Active account id.
 * @param {object} args.client - Supabase client.
 * @returns {{images: Object<string, {png: string|null, thumb: string|null}>, loading: boolean, error: string, refresh: function(): void}} Images.
 */
export function useDeckSlideImages({ model, generationMetadata, accountId, client }) {
  const paths = useMemo(() => resolveSlideImagePaths(model, generationMetadata, accountId), [model, generationMetadata, accountId]);
  // Re-sign only when the paths change, not on every edit of the model.
  const pathsKey = useMemo(() => JSON.stringify([...paths.entries()]), [paths]);
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ key: null, urls: new Map(), error: '' });

  useEffect(() => {
    const entries = JSON.parse(pathsKey);
    const wanted = entries.flatMap(([, p]) => [p.png, p.thumb]).filter(Boolean);
    if (wanted.length === 0) {
      setState({ key: `${pathsKey}:${attempt}`, urls: new Map(), error: '' });
      return undefined;
    }
    let active = true;
    signRenderPaths(client, wanted)
      .then((urls) => {
        if (active) setState({ key: `${pathsKey}:${attempt}`, urls, error: '' });
      })
      .catch((error) => {
        console.warn('Could not sign slide images:', error);
        if (active) setState({ key: `${pathsKey}:${attempt}`, urls: new Map(), error: 'The slide images could not be loaded.' });
      });
    return () => {
      active = false;
    };
  }, [pathsKey, attempt, client]);

  const images = useMemo(() => {
    const out = {};
    paths.forEach((p, key) => {
      out[key] = { png: p.png ? state.urls.get(p.png) || null : null, thumb: p.thumb ? state.urls.get(p.thumb) || null : null };
    });
    return out;
  }, [paths, state.urls]);

  const refresh = useCallback(() => setAttempt((n) => n + 1), []);
  return { images, loading: state.key !== `${pathsKey}:${attempt}`, error: state.error, refresh };
}
